import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Colors from 'styles/export/colors.module.scss';
import { VictoryArea, VictoryAxis, VictoryChart, VictoryLegend, VictoryLine } from 'victory';
import styles from './MonthlyCostChart.module.scss';
import { MonthlyCostDashboardDto, MonthlyCostItemTypeDashboardDto } from 'api/dashboard/dtos/MonthlyCostDashboardDto';
import { DashboardMonthlyCostTypeEnum } from 'api/dashboard/enums/DashboardMonthlyCostTypeEnum';
import Utils from 'common/services/Utils';
import { DASHBOARD_COLORS, DEFAULT_LANGUAGE, STORAGE } from 'Config';
import ChartLegendLabel from 'common/components/charts/ChartLegendLabel';
import ChartEmptyLabel from 'common/components/charts/ChartEmptyLabel';
import useElementSize from 'common/hooks/useElementSize';
import dayjs from 'dayjs';
import Storage from 'common/services/Storage';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import ChartAveragePill from '../components/ChartAveragePill';
import WidgetNoData from '../components/WidgetNoData';

type Props = {
	data: MonthlyCostDashboardDto;
	selectedYear: number | null;
	onMonthlyCostDashboardClick?: (startDate?: Date) => void;
};

const MonthlyCostChart = ({ data, selectedYear, onMonthlyCostDashboardClick }: Props) => {
	const { t } = useTranslation();
	const [hiddenData, setHiddenData] = useState<DashboardMonthlyCostTypeEnum[]>([]);
	const [reRenderKey, setReRenderKey] = useState(new Date().getTime());
	const [containerRef, { width, height }] = useElementSize();
	const [tooltip, setTooltip] = useState<{ dateTime: number; clientX: number; clientY: number } | null>(null);
	const [avgTooltip, setAvgTooltip] = useState<{ clientX: number; clientY: number } | null>(null);
	const svgRef = useRef<SVGSVGElement>(null);
	const locale = Storage.get(STORAGE.CURRENT_LOCALE) || DEFAULT_LANGUAGE;

	useEffect(() => {
		setReRenderKey(new Date().getTime());
	}, [data, selectedYear, width, height]);

	if (!data.items || data.items.length === 0) {
		return <WidgetNoData />;
	}

	const chartWidth = Math.max(width, 100);
	const legendHeight = 28;
	const chartHeight = Math.max(height, 60);
	const chartPadding = { top: 15, left: 70, right: 25, bottom: 35 + legendHeight };

	let itemsData = data.items
		.filter(x => !hiddenData.find(h => h === x.type));

	if (selectedYear) {
		itemsData = itemsData.map(x => ({
			...x,
			items: x.items.filter(s => dayjs(s.date).year() === selectedYear),
		}));
	}

	const allItems = Utils.flatten<MonthlyCostItemTypeDashboardDto>(itemsData.map(x => x.items));
	const allDates = allItems
		.map(x => x.date)
		.filter((date, i, self) => self.findIndex(d => d.getTime() === date.getTime()) === i);

	// Thin X-axis ticks when range is long so labels don't overlap
	const MAX_TICKS = 12;
	const TICK_STEPS = [1, 2, 3, 6, 12];
	const tickStep = TICK_STEPS.find(s => Math.ceil(allDates.length / s) <= MAX_TICKS) ?? 12;
	const sortedDates = [...allDates].sort((a, b) => a.getTime() - b.getTime());
	const tickDates = sortedDates.filter((_, i) => i % tickStep === 0);

	// Compute total per month (sum of all series) for the average line
	const monthTotals = allDates.map(date => {
		const total = itemsData.reduce((sum, series) => {
			const item = series.items.find(i => i.date.getTime() === date.getTime());
			return sum + (item ? item.value : 0);
		}, 0);
		return total;
	});
	const average = monthTotals.length > 0 ? monthTotals.reduce((a, b) => a + b, 0) / monthTotals.length : 0;
	// Use highest individual value (not stacked sum) for Y domain, but ensure average line fits
	const allValues = allItems.map(x => x.value);
	const highestIndividual = allValues.length > 0 ? Math.max(...allValues) : 0;
	const highestValue = Math.max(highestIndividual, average);

	// Y pixel of the dashed average line, used to position the transparent hit-zone.
	const plotTop = chartPadding.top;
	const plotBottom = chartHeight - chartPadding.bottom;
	const plotHeight = Math.max(plotBottom - plotTop, 0);
	const avgYPx = highestValue > 0
		? plotTop + plotHeight * (1 - average / highestValue)
		: plotBottom;
	const AVG_HIT_HALF_HEIGHT = 6;

	// Build month breakdown lookup (always using all data, even if legend hides a series)
	let allItemsForTooltip = data.items;
	if (selectedYear) {
		allItemsForTooltip = allItemsForTooltip.map(x => ({
			...x,
			items: x.items.filter(s => dayjs(s.date).year() === selectedYear),
		}));
	}
	const monthBreakdown = new Map<number, { fixed: number; variable: number; total: number }>();
	allDates.forEach(date => {
		const fixed = allItemsForTooltip
			.filter(x => x.type === DashboardMonthlyCostTypeEnum.FIXED_COST)
			.flatMap(x => x.items)
			.find(i => i.date.getTime() === date.getTime())?.value ?? 0;
		const variable = allItemsForTooltip
			.filter(x => x.type === DashboardMonthlyCostTypeEnum.VARIABLE_COST)
			.flatMap(x => x.items)
			.find(i => i.date.getTime() === date.getTime())?.value ?? 0;
		monthBreakdown.set(date.getTime(), { fixed, variable, total: fixed + variable });
	});

	const legendData = [
		...data.items.map(x => ({
			name: t(`dashboard.monthly_cost_types.${x.type}` as any),
			type: x.type,
			symbol: {
				fill: !hiddenData.find(a => a === x.type) ? DASHBOARD_COLORS.monthlyCost[x.type] : Colors.gray17,
			},
			labels: { fill: Colors.primaryDark },
			clickable: true,
		})),
	];

	const onClickLegend = (propsData: any) => {
		const type = propsData.datum.type;
		if (!type) return;

		if (hiddenData.find(x => x === type)) {
			setHiddenData(hiddenData.filter(x => x !== type));
		} else {
			setHiddenData([...hiddenData, type]);
		}
		setReRenderKey(new Date().getTime());
	};

	// Estimate legend width for centering
	const legendItemWidth = 130;
	const legendTotalWidth = legendData.length * legendItemWidth;
	const legendX = Math.max((chartWidth - legendTotalWidth) / 2, 0);

	const tooltipData = tooltip ? monthBreakdown.get(tooltip.dateTime) : null;
	const tooltipMonth = tooltip ? dayjs(tooltip.dateTime).locale(locale).format('MMM YYYY') : '';

	// Find nearest date from mouse x position on the SVG
	const findNearestDate = useCallback((evt: React.MouseEvent<SVGRectElement>) => {
		if (!svgRef.current || allDates.length === 0) return null;
		const svgRect = svgRef.current.getBoundingClientRect();
		const mouseX = evt.clientX - svgRect.left;
		const plotLeft = chartPadding.left;
		const plotRight = chartWidth - chartPadding.right;
		const plotWidth = plotRight - plotLeft;
		const ratio = (mouseX - plotLeft) / plotWidth;

		const sortedDates = [...allDates].sort((a, b) => a.getTime() - b.getTime());
		const minTime = sortedDates[0].getTime();
		const maxTime = sortedDates[sortedDates.length - 1].getTime();
		const timeRange = maxTime - minTime;
		if (timeRange === 0) return sortedDates[0];

		const targetTime = minTime + ratio * timeRange;
		let closest = sortedDates[0];
		let closestDist = Math.abs(targetTime - closest.getTime());
		for (const date of sortedDates) {
			const dist = Math.abs(targetTime - date.getTime());
			if (dist < closestDist) {
				closest = date;
				closestDist = dist;
			}
		}
		return closest;
	}, [allDates, chartWidth, chartPadding]);

	const handleMouseMove = useCallback((evt: React.MouseEvent<SVGRectElement>) => {
		const nearest = findNearestDate(evt);
		if (nearest) {
			setTooltip({ dateTime: nearest.getTime(), clientX: evt.clientX, clientY: evt.clientY });
		}
	}, [findNearestDate]);

	const handleMouseLeave = useCallback(() => {
		setTooltip(null);
	}, []);

	const handleClick = useCallback((evt: React.MouseEvent<SVGRectElement>) => {
		if (!onMonthlyCostDashboardClick) return;
		const nearest = findNearestDate(evt);
		if (nearest) {
			onMonthlyCostDashboardClick(nearest);
		}
	}, [findNearestDate, onMonthlyCostDashboardClick]);

	return (
		<div className={styles.container}>
			<div className={styles.chartContainer} ref={containerRef}>
				{width > 0 && height > 0 && (
					<svg width={width} height={height} key={'svg-' + reRenderKey} ref={svgRef}>
						{/* Average Line */}
						{itemsData.length > 0 && (
							<VictoryChart
								standalone={false}
								width={chartWidth}
								height={chartHeight}
								padding={chartPadding}
								maxDomain={{ y: highestValue }}
								minDomain={{ y: 0 }}
							>
								<VictoryLine
									style={{
										data: {
											stroke: Colors.primaryDark,
											strokeWidth: 1,
											strokeDasharray: 4,
										},
									}}
									data={[
										{ x: 0, y: average },
										{ x: 1, y: average },
									]}
								/>
								<VictoryAxis
									dependentAxis
									standalone={false}
									tickValues={monthTotals}
									style={{
										axis: { stroke: 'transparent', fill: 'none' },
										ticks: { stroke: 'transparent', fill: 'none' },
										axisLabel: { stroke: 'transparent', fill: 'none' },
									}}
									tickLabelComponent={<ChartEmptyLabel />}
								/>
							</VictoryChart>
						)}

						{/* Main area chart */}
						{itemsData.length > 0 && (
							<VictoryChart
								standalone={false}
								width={chartWidth}
								height={chartHeight}
								padding={chartPadding}
								scale={{ x: 'time' }}
								domainPadding={{ x: 20 }}
								maxDomain={{ y: highestValue }}
								minDomain={{ y: 0 }}
								key={'chart-' + reRenderKey}
							>
								{itemsData.map((item, i) => (
									<VictoryArea
										key={'area-' + item.type + '-' + i}
										data={item.items}
										x="date"
										y="value"
										interpolation="monotoneX"
										style={{
											data: {
												fill: DASHBOARD_COLORS.monthlyCost[item.type],
												fillOpacity: 0.2,
												stroke: DASHBOARD_COLORS.monthlyCost[item.type],
												strokeWidth: 2.5,
											},
										}}
									/>
								))}
								<VictoryAxis
									orientation="bottom"
									width={chartWidth}
									height={chartHeight}
									tickValues={tickDates}
									tickFormat={x => Utils.capitalize(dayjs(x).locale(locale).format('MMM')) + ' ' + dayjs(x).format('YY')}
									style={{
										tickLabels: { fill: Colors.black, userSelect: 'none', fontSize: 12, fontFamily: 'Roboto' },
										axis: { stroke: Colors.gray3 },
									}}
								/>
								<VictoryAxis
									dependentAxis
									style={{
										axis: { stroke: 'transparent' },
										grid: { stroke: '#f0f0f0', strokeDasharray: '4' },
										tickLabels: { fill: '#6b7280', fontSize: 10, fontFamily: 'Roboto' },
									}}
									tickFormat={x => Utils.formatCurrency(x)}
								/>
							</VictoryChart>
						)}

						{/* Legends */}
						<VictoryLegend
							standalone={false}
							x={legendX}
							y={chartHeight - legendHeight}
							gutter={40}
							data={legendData}
							orientation={'horizontal'}
							itemsPerRow={3}
							labelComponent={<ChartLegendLabel onClick={onClickLegend} />}
							style={{ labels: { fontSize: 12, userSelect: 'none', fontFamily: 'Roboto' } }}
						/>

						{/* Invisible overlay for mouse interaction */}
						<rect
							x={chartPadding.left}
							y={chartPadding.top}
							width={chartWidth - chartPadding.left - chartPadding.right}
							height={chartHeight - chartPadding.top - chartPadding.bottom}
							fill="transparent"
							style={{ cursor: 'pointer' }}
							onMouseMove={handleMouseMove}
							onMouseLeave={handleMouseLeave}
							onClick={handleClick}
						/>
						{/* Thin hit-zone over the dashed average line — rendered after the main overlay
						    so it captures hover events first within its band, and shows the average value. */}
						{itemsData.length > 0 && highestValue > 0 && (
							<rect
								x={chartPadding.left}
								y={avgYPx - AVG_HIT_HALF_HEIGHT}
								width={chartWidth - chartPadding.left - chartPadding.right}
								height={AVG_HIT_HALF_HEIGHT * 2}
								fill="transparent"
								style={{ cursor: 'pointer' }}
								onMouseMove={evt => {
									setAvgTooltip({ clientX: evt.clientX, clientY: evt.clientY });
									setTooltip(null);
								}}
								onMouseLeave={() => setAvgTooltip(null)}
							/>
						)}
					</svg>
				)}
			</div>

			{tooltip && tooltipData && (
				<ChartTooltipPortal clientX={tooltip.clientX} clientY={tooltip.clientY} className={styles.tooltip}>
					<div className={styles.tooltipTitle}>{Utils.capitalize(tooltipMonth)}</div>
					<div className={styles.tooltipRow}>
						<span className={styles.tooltipDot} style={{ backgroundColor: DASHBOARD_COLORS.monthlyCost.FIXED_COST }} />
						<span>{t('dashboard.monthly_cost_types.FIXED_COST')} : {Utils.formatCurrency(tooltipData.fixed)}</span>
					</div>
					<div className={styles.tooltipRow}>
						<span className={styles.tooltipDot} style={{ backgroundColor: DASHBOARD_COLORS.monthlyCost.VARIABLE_COST }} />
						<span>{t('dashboard.monthly_cost_types.VARIABLE_COST')} : {Utils.formatCurrency(tooltipData.variable)}</span>
					</div>
				</ChartTooltipPortal>
			)}

			{avgTooltip && (
				<ChartAveragePill clientX={avgTooltip.clientX} clientY={avgTooltip.clientY} color={Colors.primaryDark}>
					{t('dashboard.routes.AVERAGE_OVER' as any, { value: Utils.formatCurrency(average) })}
				</ChartAveragePill>
			)}
		</div>
	);
};

export default MonthlyCostChart;
